import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import { GAME_THEME } from '../Shared/constants/GameTheme';
import { inchesToPixels, TARGET_REAL } from './sizeUtils';
import CalibrationStep from './CalibrationStep';
import Countdown from '../Shared/components/Countdown/Countdown';

// ─── MSP Belt Standards ────────────────────────────────────────────────────────
const BELT_LEVELS = ['blue', 'purple', 'brown', 'black'];
const BELT_COLORS = {
    blue: '#3498db',
    purple: '#9b59b6',
    brown: '#8B4513',
    black: '#ecf0f1',
};

const DRILLS = [
    {
        id: '3x2',
        name: '3×2 Drill',
        subtitle: '3 yards',
        description: 'Draw and fire 3 body shots (A-zone) then 2 head shots',
        yardage: 3,
        parTimes: { blue: 3.0, purple: 2.5, brown: 2.15, black: 2.0 },
        shots: [
            { zone: 'body', index: 0 },
            { zone: 'body', index: 1 },
            { zone: 'body', index: 2 },
            { zone: 'head', index: 0 },
            { zone: 'head', index: 1 },
        ],
    },
    {
        id: '7yard',
        name: '7-Yard Draw',
        subtitle: '7 yards',
        description: 'Draw and fire 1 shot center mass (A-zone)',
        yardage: 7,
        parTimes: { blue: 1.5, purple: 1.3, brown: 1.1, black: 1.0 },
        shots: [{ zone: 'body', index: 0 }],
    },
    {
        id: 'bill',
        name: 'Bill Drill',
        subtitle: '7 yards',
        description: 'Draw and fire 6 shots center mass (A-zone)',
        yardage: 7,
        parTimes: { blue: 3.0, purple: 2.5, brown: 2.15, black: 2.0 },
        shots: [
            { zone: 'body', index: 0 },
            { zone: 'body', index: 1 },
            { zone: 'body', index: 2 },
            { zone: 'body', index: 3 },
            { zone: 'body', index: 4 },
            { zone: 'body', index: 5 },
        ],
    },
    {
        id: '25yard',
        name: '25-Yard Shot',
        subtitle: '25 yards',
        description: 'Draw and fire 1 shot center mass (A-zone)',
        yardage: 25,
        parTimes: { blue: 2.25, purple: 2.0, brown: 1.65, black: 1.5 },
        shots: [{ zone: 'body', index: 0 }],
    },
];

// Full test: run all 4 drills in sequence. Belt only if ALL pass at same level.
const FULL_DRILL = {
    id: 'full',
    name: 'MSP Full Test',
    subtitle: 'All 4 drills',
    description: 'Run all four MSP standards back-to-back. You must pass every drill at the same belt level to earn it.',
    isFullTest: true,
};

const ALL_DRILLS = [...DRILLS, FULL_DRILL];

// ─── Belt Eval ────────────────────────────────────────────────────────────────
function evaluateBelt(drill, timeSeconds, parTimes) {
    if (timeSeconds === null) return null;
    const p = parTimes ?? drill.parTimes;
    if (timeSeconds <= p.black) return 'black';
    if (timeSeconds <= p.brown) return 'brown';
    if (timeSeconds <= p.purple) return 'purple';
    if (timeSeconds <= p.blue) return 'blue';
    return 'miss';
}

// Full test: highest belt where ALL 4 drills are within par.
function evaluateFullBelt(times, drills, effectiveParFn) {
    for (const belt of ['black', 'brown', 'purple', 'blue']) {
        const allPassed = drills.every((d, i) => times[i] != null && times[i] <= effectiveParFn(d)[belt]);
        if (allPassed) return belt;
    }
    return 'miss';
}

// ─── Component ─────────────────────────────────────────────────────────────────
const ModernSamurai = () => {
    const canvasRef = useRef(null);
    const { beep, ping } = useAudio();

    // ── Calibration state ──────────────────────────────────────────────────────
    const savedCal = (() => {
        try { return JSON.parse(localStorage.getItem('msp_cal')); } catch { return null; }
    })();
    const [screenWidthIn, setScreenWidthIn] = useState(savedCal?.screenWidthIn ?? 60);
    const [viewDistIn,    setViewDistIn]    = useState(savedCal?.viewDistIn    ?? 72);
    const [retentionHolster, setRetentionHolster] = useState(savedCal?.retentionHolster ?? false);
    const [selectedDrillIndex, setSelectedDrillIndex] = useState(0);

    // ── Game state ────────────────────────────────────────────────────────────
    const [gameState, setGameState] = useState(savedCal ? 'menu' : 'calibrating');
    const [results, setResults] = useState([]);

    // ── Earned belt (persisted) ───────────────────────────────────────────────
    const BELT_RANK = { blue: 1, purple: 2, brown: 3, black: 4 };
    const [earnedBelt, setEarnedBelt] = useState(
        () => localStorage.getItem('msp_earned_belt') || null
    );
    const saveEarnedBelt = useCallback((belt) => {
        if (!belt || belt === 'miss') return;
        setEarnedBelt(prev => {
            const prevRank = BELT_RANK[prev] ?? 0;
            const newRank  = BELT_RANK[belt] ?? 0;
            if (newRank > prevRank) {
                localStorage.setItem('msp_earned_belt', belt);
                return belt;
            }
            return prev;
        });
    }, []); // eslint-disable-next-line

    // ── Laser input latency offset ───────────────────────────────────────────
    // Read from TestLatency calibrator (‘calibratedLatency’ key, stored in ms).
    // This is the extra delay the laser clicker adds vs a direct mouse click.
    // We subtract it from each recorded hit time to get the "true" reaction time.
    const [inputLatencyMs] = useState(
        () => Math.max(0, Number(localStorage.getItem('calibratedLatency') || 0))
    );

    // ── Full-test state ───────────────────────────────────────────────────────
    // fullTestDrillIdxRef: which of the 4 drills (0-3) we're currently running
    // fullTestTimesRef: accumulated times for each completed drill
    const fullTestDrillIdxRef = useRef(0);
    const fullTestTimesRef = useRef([]); // number[]
    const [fullTestResults, setFullTestResults] = useState(null); // set on completion

    // activeDrillRef: the actual drill being executed right now
    // (in full-test mode this cycles through DRILLS[0..3])
    const activeDrillRef = useRef(DRILLS[selectedDrillIndex]);
    const drillRef = useRef(ALL_DRILLS[selectedDrillIndex]);

    useEffect(() => {
        drillRef.current = ALL_DRILLS[selectedDrillIndex];
        if (!drillRef.current.isFullTest) {
            activeDrillRef.current = drillRef.current;
        }
    }, [selectedDrillIndex]);

    const stateRef = useRef({
        running: false,
        phase: 'idle',
        startTime: 0,
        shotsHit: 0,
        totalShots: 0,
        targets: [],
        endTime: null,
        drill: null,
    });

    // ── Calibration ───────────────────────────────────────────────────────────
    const handleCalibrationApply = useCallback(({ screenWidthIn: sw, viewDistIn: vd }) => {
        setScreenWidthIn(sw);
        setViewDistIn(vd);
        try { localStorage.setItem('msp_cal', JSON.stringify({ screenWidthIn: sw, viewDistIn: vd, retentionHolster })); } catch { /* ignore */ }
        setGameState('menu');
    }, [retentionHolster]);

    // ── Retention holster: +0.2s to all par times ─────────────────────────────
    const RETENTION_PENALTY = 0.2;
    const effectivePar = useCallback((drill) => {
        if (!retentionHolster) return drill.parTimes;
        return Object.fromEntries(Object.entries(drill.parTimes).map(([k, v]) => [k, v + RETENTION_PENALTY]));
    }, [retentionHolster]);

    // ── Helper: Build Targets ─────────────────────────────────────────────────
    const buildTargets = useCallback((drill, canvas) => {
        const w = canvas.width;
        const h = canvas.height;

        function px(realIn) {
            return inchesToPixels(realIn, w, screenWidthIn, viewDistIn, drill.yardage);
        }

        const bx = w / 2;
        const by = h * 0.62;

        const azW = px(TARGET_REAL.bodyZoneWidth);
        const azH = px(TARGET_REAL.bodyZoneHeight);
        const hdW = px(TARGET_REAL.headWidth);
        const hdH = px(TARGET_REAL.headHeight);
        const hx = bx;
        const hy = by - px(TARGET_REAL.headOffsetY);

        return drill.shots.map((shot) => {
            if (shot.zone === 'body') {
                return { zone: 'body', cx: bx, cy: by, halfW: azW / 2, halfH: azH / 2, hit: false, hitX: null, hitY: null, hitTime: null };
            } else {
                return { zone: 'head', cx: hx, cy: hy, halfW: hdW / 2, halfH: hdH / 2, hit: false, hitX: null, hitY: null, hitTime: null };
            }
        });
    }, [screenWidthIn, viewDistIn]);

    // ── Start Drill ───────────────────────────────────────────────────────────
    const startDrill = useCallback(() => {
        const drill = activeDrillRef.current;
        const canvas = canvasRef.current;
        if (!canvas || !drill) return;

        const state = stateRef.current;
        state.drill = drill;
        state.targets = buildTargets(drill, canvas);
        state.totalShots = drill.shots.length;
        state.shotsHit = 0;
        state.endTime = null;
        state.phase = 'playing';
        state.running = true;
        state.startTime = performance.now();

        beep(1000, 0.1);
        setGameState('playing');
    }, [buildTargets, beep]);

    // ── Begin standby ─────────────────────────────────────────────────────────
    const beginStandby = useCallback(() => {
        setGameState('standby');
    }, []);

    // ── Begin full test ───────────────────────────────────────────────────────
    const beginFullTest = useCallback(() => {
        fullTestDrillIdxRef.current = 0;
        fullTestTimesRef.current = [];
        setFullTestResults(null);
        activeDrillRef.current = DRILLS[0];
        setGameState('standby');
    }, []);

    // ── Game Loop ──────────────────────────────────────────────────────────────
    const loop = useCallback((now) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;
        const state = stateRef.current;

        ctx.fillStyle = GAME_THEME.BACKGROUND_COLOR;
        ctx.fillRect(0, 0, w, h);

        if (state.phase === 'idle' || state.phase === 'done') return;

        if (state.phase === 'playing') {
            const elapsed = now - state.startTime;
            drawTargets(ctx, state.targets, w, h, true, 'playing', now, elapsed);

            ctx.fillStyle = '#fff';
            ctx.font = `bold ${Math.max(22, w * 0.03)}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText((elapsed / 1000).toFixed(3) + 's', w / 2, h * 0.08);

            ctx.fillStyle = 'rgba(255,255,255,0.5)';
            ctx.font = `${Math.max(14, w * 0.016)}px Inter, sans-serif`;
            ctx.fillText(state.drill.name + ' — ' + state.drill.subtitle, w / 2, h * 0.14);

            const remaining = state.totalShots - state.shotsHit;
            ctx.fillStyle = remaining === 0 ? '#2ecc71' : 'rgba(255,255,255,0.4)';
            ctx.font = `${Math.max(13, w * 0.014)}px Inter, sans-serif`;
            ctx.fillText(`Shots: ${state.shotsHit} / ${state.totalShots}`, w / 2, h * 0.19);

            // Full test progress indicator
            const isFullTest = drillRef.current?.isFullTest;
            if (isFullTest) {
                const idx = fullTestDrillIdxRef.current;
                ctx.fillStyle = 'rgba(255,255,255,0.3)';
                ctx.font = `${Math.max(11, w * 0.012)}px Inter, sans-serif`;
                ctx.fillText(`Drill ${idx + 1} of 4`, w / 2, h * 0.24);
            }
        }
    }, []);

    useGameLoop(loop, true);

    // ── Canvas click ──────────────────────────────────────────────────────────
    const handleCanvasClick = useCallback((e) => {
        const state = stateRef.current;
        if (state.phase !== 'playing') return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const sx = canvas.width / rect.width;
        const sy = canvas.height / rect.height;
        const mx = (e.clientX - rect.left) * sx;
        const my = (e.clientY - rect.top) * sy;

        const drill = state.drill;
        const now = performance.now();

        const nextIdx = state.targets.findIndex(t => !t.hit);
        if (nextIdx === -1) return;

        const t = state.targets[nextIdx];
        if (Math.abs(mx - t.cx) <= t.halfW && Math.abs(my - t.cy) <= t.halfH) {
            t.hit = true;
            t.hitX = mx;
            t.hitY = my;
            t.hitTime = Math.max(0, (now - state.startTime) / 1000 - inputLatencyMs / 1000);
            state.shotsHit++;
            ping();

            if (state.shotsHit >= state.totalShots) {
                state.phase = 'done';
                state.endTime = now;
                state.running = false;

                const totalTime = (state.endTime - state.startTime) / 1000;
                const isFullTest = drillRef.current?.isFullTest;

                if (isFullTest) {
                    // Accumulate this drill's time
                    const idx = fullTestDrillIdxRef.current;
                    fullTestTimesRef.current[idx] = totalTime;
                    const nextDrillIdx = idx + 1;

                    if (nextDrillIdx < DRILLS.length) {
                        // More drills — advance and standby
                        fullTestDrillIdxRef.current = nextDrillIdx;
                        activeDrillRef.current = DRILLS[nextDrillIdx];
                        // Small delay then standby
                        setTimeout(() => {
                            stateRef.current.phase = 'idle';
                            setGameState('standby');
                        }, 800);
                    } else {
                        // All done — evaluate
                        const times = [...fullTestTimesRef.current];
                        const overallBelt = evaluateFullBelt(times, DRILLS, effectivePar);
                        const perDrill = DRILLS.map((d, i) => ({
                            drill: d,
                            time: times[i],
                            belt: evaluateBelt(d, times[i], effectivePar(d)),
                        }));
                        setFullTestResults({ perDrill, overallBelt });
                        saveEarnedBelt(overallBelt);
                        setResults(prev => [{
                            drill: 'full',
                            drillName: 'MSP Full Test',
                            time: Math.max(...times),
                            belt: overallBelt,
                        }, ...prev.slice(0, 19)]);
                        setTimeout(() => {
                            stateRef.current.phase = 'idle';
                            setGameState('fullTestResult');
                        }, 800);
                    }
                } else {
                    // Regular single drill
                    const belt = evaluateBelt(drill, totalTime, effectivePar(drill));
                    saveEarnedBelt(belt);
                    setResults(prev => [{
                        drill: drill.id,
                        drillName: drill.name,
                        time: totalTime,
                        belt,
                        shots: state.shotsHit,
                        parTimes: drill.parTimes,
                    }, ...prev.slice(0, 19)]);
                    setGameState('result');
                }
            }
        }
    }, [ping, effectivePar]);

    // ── Draw Targets ──────────────────────────────────────────────────────────
    function drawTargets(ctx, targets, w, h, active, phase) {
        if (!targets || targets.length === 0) return;

        const bodyTargets = targets.filter(t => t.zone === 'body');
        const headTargets = targets.filter(t => t.zone === 'head');
        const alpha = phase === 'waiting' ? 0.3 : 1.0;

        // A-zone (body) — 5.9" × 11"
        if (bodyTargets.length > 0) {
            const t0 = bodyTargets[0];
            const { cx, cy, halfW, halfH } = t0;
            ctx.globalAlpha = alpha;
            ctx.fillStyle = phase === 'done' ? '#1a1a1a' : GAME_THEME.TARGET_FILL;
            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.rect(cx - halfW, cy - halfH, halfW * 2, halfH * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,0.22)';
            ctx.font = `bold ${Math.max(10, halfW * 0.28)}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('A-ZONE', cx, cy + halfH - Math.max(6, halfH * 0.1));
        }

        // Head box — 4" × 6"
        if (headTargets.length > 0) {
            const t0 = headTargets[0];
            const { cx, cy, halfW, halfH } = t0;
            ctx.globalAlpha = alpha;
            ctx.fillStyle = phase === 'done' ? '#1a1a1a' : GAME_THEME.TARGET_FILL;
            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.rect(cx - halfW, cy - halfH, halfW * 2, halfH * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,0.22)';
            ctx.font = `bold ${Math.max(8, halfW * 0.35)}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('HEAD', cx, cy + halfH * 0.35);
        }

        // Hit markers
        ctx.globalAlpha = 1.0;
        targets.forEach((t, i) => {
            if (t.hit && t.hitX !== null) {
                ctx.fillStyle = '#e74c3c';
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(t.hitX, t.hitY, Math.max(5, Math.min(10, t.halfW * 0.25)), 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
                ctx.fillStyle = '#fff';
                ctx.font = `${Math.max(10, t.halfW * 0.22)}px Inter, monospace`;
                ctx.textAlign = 'center';
                ctx.fillText(t.hitTime.toFixed(3) + 's', t.hitX, t.hitY - Math.max(8, t.halfW * 0.22));
            } else if (!t.hit) {
                ctx.fillStyle = 'rgba(255,255,100,0.7)';
                ctx.font = `bold ${Math.max(14, t.halfW * 0.4)}px Inter, sans-serif`;
                ctx.textAlign = 'center';
                ctx.fillText(i + 1, t.cx, t.cy + Math.max(5, t.halfH * 0.1));
            }
        });
        ctx.globalAlpha = 1.0;
    }

    // ── Sidebar ───────────────────────────────────────────────────────────────
    const sidebarContent = (
        <>
            <button
                onClick={() => setGameState('calibrating')}
                style={{
                    display: 'block', width: '100%',
                    background: gameState === 'calibrating' ? '#1a3a1a' : '#181818',
                    color: gameState === 'calibrating' ? '#2ecc71' : '#888',
                    border: `1px solid ${gameState === 'calibrating' ? '#2ecc71' : '#333'}`,
                    borderRadius: '6px', padding: '8px 10px', marginBottom: '12px',
                    cursor: 'pointer', fontSize: '12px', textAlign: 'left', letterSpacing: '0.5px',
                }}
            >
                📐 Calibrate Screen Setup
                <div style={{ fontSize: '10px', color: '#555', marginTop: '2px' }}>
                    Screen: {screenWidthIn.toFixed(1)}&quot; &nbsp;|&nbsp; Dist: {(viewDistIn / 12).toFixed(1)} ft
                </div>
            </button>

            {/* Laser latency offset */}
            <div style={{
                background: '#181818', border: `1px solid ${inputLatencyMs > 0 ? '#2ecc7133' : '#2a2a2a'}`,
                borderRadius: '6px', padding: '8px 10px', marginBottom: '12px', fontSize: '11px',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#666' }}>⚡ Laser offset</span>
                    <span style={{ fontFamily: 'monospace', color: inputLatencyMs > 0 ? '#2ecc71' : '#444', fontWeight: 'bold' }}>
                        {inputLatencyMs > 0 ? `−${inputLatencyMs}ms` : 'not set'}
                    </span>
                </div>
                {inputLatencyMs === 0 && (
                    <div style={{ marginTop: '4px', fontSize: '10px', color: '#444' }}>
                        Run <a href="/test-latency" style={{ color: '#3498db', textDecoration: 'none' }}>Latency Calibrator</a> to correct click times
                    </div>
                )}
            </div>

            <div style={{ marginBottom: '16px' }}>
                <div style={{ color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Drill</div>
                {ALL_DRILLS.map((d, i) => (
                    <button
                        key={d.id}
                        onClick={() => setSelectedDrillIndex(i)}
                        style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            background: selectedDrillIndex === i ? (d.isFullTest ? '#1a3a1a' : '#1a3a5c') : '#222',
                            color: selectedDrillIndex === i ? (d.isFullTest ? '#2ecc71' : '#5dade2') : '#ccc',
                            border: selectedDrillIndex === i ? `1px solid ${d.isFullTest ? '#2ecc71' : '#5dade2'}` : '1px solid #333',
                            borderRadius: '6px', padding: '8px 10px', marginBottom: '5px',
                            cursor: 'pointer', fontSize: '13px',
                            fontWeight: selectedDrillIndex === i ? 'bold' : 'normal',
                        }}
                    >
                        <div>{d.name}</div>
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                            {d.isFullTest ? 'All standards • Earn your belt' : `${d.subtitle} — ${d.shots.length} shot${d.shots.length > 1 ? 's' : ''}`}
                        </div>
                    </button>
                ))}
            </div>

            {/* Retention holster */}
            <div style={{ borderTop: '1px solid #333', paddingTop: '10px', marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: retentionHolster ? '#f39c12' : '#aaa' }}>
                    <input
                        type="checkbox"
                        checked={retentionHolster}
                        onChange={e => {
                            const val = e.target.checked;
                            setRetentionHolster(val);
                            try {
                                const prev = JSON.parse(localStorage.getItem('msp_cal') || '{}');
                                localStorage.setItem('msp_cal', JSON.stringify({ ...prev, retentionHolster: val }));
                            } catch { /* ignore */ }
                        }}
                        style={{ accentColor: '#f39c12' }}
                    />
                    Retention Holster
                    {retentionHolster && <span style={{ color: '#f39c12', fontSize: '10px', marginLeft: 'auto' }}>+0.2s</span>}
                </label>
            </div>

            {/* Par times */}
            {!ALL_DRILLS[selectedDrillIndex].isFullTest && (
                <div style={{ borderTop: '1px solid #333', paddingTop: '12px', marginBottom: '12px' }}>
                    <div style={{ color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                        Par Times {retentionHolster && <span style={{ color: '#f39c12' }}>(+0.2s)</span>}
                    </div>
                    {BELT_LEVELS.map(belt => (
                        <div key={belt} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ color: BELT_COLORS[belt], fontSize: '12px', fontWeight: 'bold', textTransform: 'capitalize' }}>{belt}</span>
                            <span style={{ color: '#ccc', fontSize: '12px', fontFamily: 'monospace' }}>≤ {effectivePar(DRILLS[selectedDrillIndex])[belt].toFixed(2)}s</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Recent results */}
            {results.length > 0 && (
                <div style={{ borderTop: '1px solid #333', paddingTop: '12px' }}>
                    <div style={{ color: '#aaa', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Recent Runs</div>
                    {results.slice(0, 8).map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
                            <span style={{ color: '#888' }}>{r.drillName}</span>
                            <span style={{ fontFamily: 'monospace', color: r.belt === 'miss' ? '#e74c3c' : BELT_COLORS[r.belt] }}>
                                {r.time.toFixed(3)}s{r.belt && r.belt !== 'miss' && ` (${r.belt})`}{r.belt === 'miss' && ' ✗'}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );

    // ── Overlays ──────────────────────────────────────────────────────────────
    const renderOverlay = () => {
        if (gameState === 'calibrating') {
            return (
                <CalibrationStep
                    initialScreenWidthIn={screenWidthIn}
                    initialViewDistIn={viewDistIn}
                    onApply={handleCalibrationApply}
                    onSkip={() => setGameState('menu')}
                />
            );
        }

        if (gameState === 'menu') {
            const d = ALL_DRILLS[selectedDrillIndex];
            return (
                <div style={overlayStyle}>
                    <div style={overlayBoxStyle}>
                        <h2 style={{ margin: '0 0 6px', color: d.isFullTest ? '#2ecc71' : '#5dade2', fontSize: '22px' }}>{d.name}</h2>
                        <p style={{ color: '#aaa', margin: '0 0 16px', fontSize: '13px' }}>{d.description}</p>

                        {d.isFullTest ? (
                            // Full test: show all 4 drills and their par times for each belt
                            <div style={{ width: '100%', marginBottom: '16px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr repeat(4, auto)', gap: '4px 8px', fontSize: '11px', marginBottom: '6px' }}>
                                    <div style={{ color: '#555' }}></div>
                                    {BELT_LEVELS.map(b => <div key={b} style={{ color: BELT_COLORS[b], fontWeight: 'bold', textAlign: 'right', textTransform: 'capitalize' }}>{b}</div>)}
                                    {DRILLS.map((drill) => (
                                        <React.Fragment key={drill.id}>
                                            <div style={{ color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{drill.name}</div>
                                            {BELT_LEVELS.map(b => (
                                                <div key={b} style={{ color: '#888', fontFamily: 'monospace', textAlign: 'right' }}>
                                                    {effectivePar(drill)[b].toFixed(2)}
                                                </div>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                                {BELT_LEVELS.map(belt => (
                                    <div key={belt} style={{ background: '#1e1e1e', border: `1px solid ${BELT_COLORS[belt]}44`, borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                                        <div style={{ color: BELT_COLORS[belt], fontWeight: 'bold', fontSize: '13px', textTransform: 'capitalize' }}>{belt}</div>
                                        <div style={{ color: '#ccc', fontFamily: 'monospace', fontSize: '15px' }}>≤ {effectivePar(d)[belt].toFixed(2)}s</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={d.isFullTest ? beginFullTest : beginStandby}
                            style={{ ...startBtnStyle, ...(d.isFullTest ? { background: '#1a3a1a', color: '#2ecc71', borderColor: '#2ecc71' } : {}) }}
                        >
                            {d.isFullTest ? 'START FULL TEST' : 'START DRILL'}
                        </button>
                    </div>
                </div>
            );
        }

        if (gameState === 'standby') {
            const isFullTest = drillRef.current?.isFullTest;
            const label = isFullTest
                ? `Drill ${fullTestDrillIdxRef.current + 1} of 4 — ${DRILLS[fullTestDrillIdxRef.current]?.name}`
                : null;
            return (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <Countdown onFinish={startDrill} minDelay={isFullTest ? 5500 : 3500} maxDelay={isFullTest ? 7500 : 5500} />
                    {label && (
                        <div style={{ position: 'absolute', bottom: '20%', left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '14px', pointerEvents: 'none' }}>
                            {label}
                        </div>
                    )}
                </div>
            );
        }

        if (gameState === 'result') {
            const r = results[0];
            const belt = r?.belt;
            const passed = belt && belt !== 'miss';
            const drill = activeDrillRef.current;
            return (
                <div style={overlayStyle}>
                    <div style={overlayBoxStyle}>
                        <div style={{ fontSize: '42px', fontWeight: 'bold', fontFamily: 'monospace', color: passed ? BELT_COLORS[belt] : '#e74c3c', marginBottom: '6px' }}>
                            {r?.time.toFixed(3)}s
                        </div>
                        {passed
                            ? <div style={{ color: BELT_COLORS[belt], fontSize: '18px', fontWeight: 'bold', textTransform: 'capitalize', marginBottom: '6px' }}>{belt} Belt</div>
                            : <div style={{ color: '#e74c3c', fontSize: '16px', marginBottom: '6px' }}>Over par — keep training!</div>
                        }
                        <div style={{ color: '#888', fontSize: '12px', marginBottom: '16px' }}>{drill?.name} / {drill?.subtitle}</div>
                        <div style={{ marginBottom: '16px', width: '100%' }}>
                            {BELT_LEVELS.map(b => {
                                const par = effectivePar(drill)[b];
                                const achieved = r?.time <= par;
                                return (
                                    <div key={b} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                        <div style={{ color: achieved ? BELT_COLORS[b] : '#444', width: '52px', fontSize: '11px', textTransform: 'capitalize', fontWeight: 'bold' }}>{b}</div>
                                        <div style={{ height: '6px', flex: 1, background: '#222', borderRadius: '3px', overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.min(100, (par / (r?.time || 1)) * 100)}%`, background: achieved ? BELT_COLORS[b] : '#444', borderRadius: '3px' }} />
                                        </div>
                                        <div style={{ color: '#555', fontSize: '11px', fontFamily: 'monospace', width: '44px' }}>≤{par.toFixed(2)}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={beginStandby} style={startBtnStyle}>AGAIN</button>
                            <button onClick={() => setGameState('menu')} style={{ ...startBtnStyle, background: '#222', border: '1px solid #444' }}>MENU</button>
                        </div>
                    </div>
                </div>
            );
        }

        if (gameState === 'fullTestResult' && fullTestResults) {
            const { perDrill, overallBelt } = fullTestResults;
            const earned = overallBelt !== 'miss';
            return (
                <div style={overlayStyle}>
                    <div style={{ ...overlayBoxStyle, maxWidth: '420px' }}>
                        {/* Overall verdict */}
                        <div style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'monospace', color: earned ? BELT_COLORS[overallBelt] : '#e74c3c', marginBottom: '4px' }}>
                            {earned ? `${overallBelt.toUpperCase()} BELT` : 'NO BELT'}
                        </div>
                        <div style={{ color: '#888', fontSize: '12px', marginBottom: '16px' }}>MSP Full Test</div>

                        {/* Per-drill breakdown */}
                        <div style={{ width: '100%', marginBottom: '16px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto repeat(4, auto)', gap: '4px 8px', fontSize: '11px', marginBottom: '6px', color: '#555' }}>
                                <div>Drill</div>
                                <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>Time</div>
                                {BELT_LEVELS.map(b => <div key={b} style={{ color: BELT_COLORS[b], textAlign: 'center', textTransform: 'capitalize' }}>{b[0].toUpperCase()}</div>)}
                            </div>
                            {perDrill.map(({ drill, time, belt: drillBelt }) => {
                                const pars = effectivePar(drill);
                                return (
                                    <div key={drill.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto repeat(4, auto)', gap: '4px 8px', fontSize: '11px', marginBottom: '4px', alignItems: 'center' }}>
                                        <div style={{ color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{drill.name}</div>
                                        <div style={{ fontFamily: 'monospace', textAlign: 'right', color: drillBelt === 'miss' ? '#e74c3c' : BELT_COLORS[drillBelt] }}>
                                            {time?.toFixed(3)}s
                                        </div>
                                        {BELT_LEVELS.map(b => (
                                            <div key={b} style={{ textAlign: 'center', fontSize: '13px' }}>
                                                {time <= pars[b] ? <span style={{ color: BELT_COLORS[b] }}>✓</span> : <span style={{ color: '#444' }}>✗</span>}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={beginFullTest} style={{ ...startBtnStyle, background: '#1a3a1a', color: '#2ecc71', borderColor: '#2ecc71' }}>RUN AGAIN</button>
                            <button onClick={() => setGameState('menu')} style={{ ...startBtnStyle, background: '#222', border: '1px solid #444' }}>MENU</button>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    const overlayStyle = {
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.75)', zIndex: 10, backdropFilter: 'blur(2px)',
    };
    const overlayBoxStyle = {
        background: '#1a1a2e', border: '1px solid #2c3e50', borderRadius: '12px',
        padding: '24px', maxWidth: '340px', width: '90%',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
    };
    const startBtnStyle = {
        background: '#1a3a5c', color: '#5dade2', border: '1px solid #5dade2',
        borderRadius: '6px', padding: '10px 24px', fontSize: '14px',
        fontWeight: 'bold', cursor: 'pointer', letterSpacing: '1px',
    };

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={gameState === 'playing'}
        >
            {renderOverlay()}
            {/* Earned belt badge — bottom-left */}
            {earnedBelt && (
                <div style={{
                    position: 'absolute',
                    bottom: '16px',
                    left: '16px',
                    background: 'rgba(0,0,0,0.65)',
                    border: `1px solid ${BELT_COLORS[earnedBelt]}66`,
                    borderRadius: '8px',
                    padding: '6px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backdropFilter: 'blur(4px)',
                    pointerEvents: 'none',
                    zIndex: 5,
                }}>
                    <div style={{
                        width: '10px', height: '10px', borderRadius: '50%',
                        background: BELT_COLORS[earnedBelt],
                        boxShadow: `0 0 6px ${BELT_COLORS[earnedBelt]}`,
                    }} />
                    <span style={{ color: BELT_COLORS[earnedBelt], fontSize: '12px', fontWeight: 'bold', textTransform: 'capitalize', letterSpacing: '0.5px' }}>
                        {earnedBelt} Belt
                    </span>
                </div>
            )}
        </GameLayout>
    );
};

export default ModernSamurai;
